// Staff Messaging Phase 1 (1.12.235-beta): @mentions in a conversation's internal notes.
// Pure helpers for the "@" picker in the note box, the stored mention list and the "@Name" chips in a saved note.
// Who is actually alerted is decided by the database (migration 299: note_mentions_after_save): nothing here can widen it.

export const MENTION_LIMIT = 20;
export const MENTION_PICKER_MAX = 8;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function str(value) {
    return typeof value === 'string' ? value : '';
}

/**
 * The "@query" being typed right before the caret, or null when the caret is not in one.
 * "@" counts only at the start or after whitespace / an opening bracket (so "val@ride.com" never opens the picker), and the query
 * stops at a line break. Names have spaces ("@Lynette Br"), so up to 40 characters with spaces are allowed.
 * @returns {{ start: number, end: number, query: string } | null}
 */
export function activeMentionQuery(text, caret) {
    const value = str(text);
    const end = Number.isInteger(caret) ? Math.max(0, Math.min(caret, value.length)) : value.length;
    const before = value.slice(0, end);
    const at = before.lastIndexOf('@');
    if (at < 0) return null;
    if (at > 0 && !/[\s([{"']/.test(before[at - 1])) return null;
    const query = before.slice(at + 1);
    if (query.length > 40 || /[\r\n@]/.test(query) || /^\s/.test(query) || /\s{2,}$/.test(query)) return null;
    return { start: at, end, query };
}

/** People whose name matches the query (any word starting with it, ignoring case and accents), best matches first. */
export function filterMentionCandidates(candidates = [], query = '', limit = MENTION_PICKER_MAX) {
    const fold = (value) => str(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    const wanted = fold(query);
    const rows = (Array.isArray(candidates) ? candidates : []).filter((row) => row && UUID.test(str(row.user_id)) && str(row.name).trim());
    const scored = rows.map((row) => {
        const name = fold(row.name);
        if (!wanted) return { row, score: 2 };
        if (name.startsWith(wanted)) return { row, score: 0 };
        if (name.split(/\s+/).some((word) => word.startsWith(wanted))) return { row, score: 1 };
        return null;
    }).filter(Boolean);
    scored.sort((left, right) => left.score - right.score || fold(left.row.name).localeCompare(fold(right.row.name)));
    return scored.slice(0, limit).map((entry) => entry.row);
}

/**
 * Put the chosen person into the text in place of "@query": "@Lynette Brownlee " (with a trailing space), caret after it.
 * @returns {{ text: string, caret: number, mention: { user_id: string, label: string } }}
 */
export function applyMention(text, range, person) {
    const value = str(text);
    const label = str(person?.name).trim();
    const start = Math.max(0, Math.min(range?.start ?? value.length, value.length));
    const end = Math.max(start, Math.min(range?.end ?? value.length, value.length));
    const after = value.slice(end);
    const insert = `@${label}${after.startsWith(' ') ? '' : ' '}`;
    const next = `${value.slice(0, start)}${insert}${after}`;
    return { text: next, caret: start + insert.length + (after.startsWith(' ') ? 1 : 0), mention: { user_id: str(person?.user_id), label } };
}

/** The mentions still present in the text ("@Label" still typed), each person once, at most MENTION_LIMIT, in text order. */
export function pruneMentions(text, mentions = []) {
    const value = str(text);
    const seen = new Set();
    const kept = [];
    (Array.isArray(mentions) ? mentions : []).forEach((mention) => {
        const userId = str(mention?.user_id);
        const label = str(mention?.label).trim();
        if (!UUID.test(userId) || !label || seen.has(userId)) return;
        if (!value.includes(`@${label}`)) return;
        seen.add(userId);
        kept.push({ user_id: userId.toLowerCase(), label });
    });
    return kept
        .sort((left, right) => value.indexOf(`@${left.label}`) - value.indexOf(`@${right.label}`))
        .slice(0, MENTION_LIMIT);
}

/** A stored note's mentions, cleaned (bad ids dropped). Accepts [{user_id,label}] or a plain id list. */
export function normalizeNoteMentions(value) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.map((entry) => (typeof entry === 'string'
        ? { user_id: entry, label: '' }
        : { user_id: str(entry?.user_id || entry?.userId), label: str(entry?.label).trim() }))
        .filter((entry) => {
            if (!UUID.test(entry.user_id)) return false;
            const key = entry.user_id.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .map((entry) => ({ user_id: entry.user_id.toLowerCase(), label: entry.label }))
        .slice(0, MENTION_LIMIT);
}

/**
 * Split a saved note's text into plain runs and "@Name" chips, for the mentions it stores (never by guessing from any "@word").
 * @returns {Array<{ type: 'text', text: string } | { type: 'mention', text: string, userId: string }>}
 */
export function noteSegments(text, mentions = []) {
    const value = str(text);
    const labels = normalizeNoteMentions(mentions).filter((mention) => mention.label)
        .sort((left, right) => right.label.length - left.label.length);
    if (!labels.length) return value ? [{ type: 'text', text: value }] : [];
    const out = [];
    let index = 0;
    while (index < value.length) {
        let match = null;
        let at = -1;
        for (const mention of labels) {
            const found = value.indexOf(`@${mention.label}`, index);
            if (found >= 0 && (at < 0 || found < at)) { at = found; match = mention; }
        }
        if (!match) break;
        if (at > index) out.push({ type: 'text', text: value.slice(index, at) });
        out.push({ type: 'mention', text: `@${match.label}`, userId: match.user_id });
        index = at + match.label.length + 1;
    }
    if (index < value.length) out.push({ type: 'text', text: value.slice(index) });
    return out;
}

/** "Lynette can't see this conversation — invite them first" (one line per person). */
export function cantSeeMessage(name) {
    const who = str(name).trim() || 'They';
    return `${who} can't see this conversation — invite them first`;
}

/** The note id in a link such as "/communications?session=<id>&note=<note id>", or ''. Only safe note ids. */
export function noteIdFromSearch(search = '') {
    try {
        const value = new URLSearchParams(str(search)).get('note') || '';
        return /^[A-Za-z0-9_-]{1,120}$/.test(value) ? value : '';
    } catch {
        return '';
    }
}

/** Load the @ picker list for one conversation: [{ user_id, name, can_view }]. Never throws: an error gives []. */
export async function loadMentionCandidates(client, sessionId) {
    if (!client?.rpc || !sessionId) return [];
    try {
        const { data, error } = await client.rpc('note_mention_candidates', { p_session: sessionId });
        if (error || !Array.isArray(data)) return [];
        return data.filter((row) => row && UUID.test(str(row.user_id))).map((row) => ({
            user_id: str(row.user_id).toLowerCase(), name: str(row.name).trim() || 'Staff', can_view: row.can_view === true,
        }));
    } catch {
        return [];
    }
}

/** The conversations with a mention for me I have not opened yet (a Set of session ids). Never throws. */
export async function loadUnseenMentionSessions(client) {
    if (!client?.rpc) return new Set();
    try {
        const { data, error } = await client.rpc('my_unseen_note_mentions');
        if (error || !Array.isArray(data)) return new Set();
        return new Set(data.map((row) => str(row?.session_id).toLowerCase()).filter(Boolean));
    } catch {
        return new Set();
    }
}

/** Opening a conversation clears my "@" marker for it. Never throws. */
export async function markMentionsSeen(client, sessionId) {
    if (!client?.rpc || !sessionId) return 0;
    try {
        const { data, error } = await client.rpc('mark_note_mentions_seen', { p_session: sessionId });
        return error ? 0 : Number(data) || 0;
    } catch {
        return 0;
    }
}
